import { Box } from '@mantine/core';
import { CSSProperties, ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY } from '../../graphql';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { useHubFilterValues } from '../../hooks/useHubFilterValues';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { QUEUE_PAGE_ROWS, useQueuePage } from '../../hooks/useQueuePage';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  buildUpcomingConsultFilter,
  isUpcomingConsult,
  upcomingConsultSubscriptionParams,
} from '../../services/preConsultationFilters';
import {
  buildUpcomingConsultRows,
  PacQueueAppointmentResult,
  PreConsultationLeadRow,
} from '../../services/preConsultationRows';
import {
  isQueueFiltered,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  TRACK_FILTER_OPTIONS,
  withPatientFilters,
} from '../../services/queueFilters';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { upcomingConsultColumn } from './postConsultColumns';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's Pre-consultation tab: one row per patient with an upcoming
 * consult (BAR-2811), on the shared row (BAR-2812), paged 100 at a time with a
 * true total (BAR-2880). The legacy
 * `components/PreConsultationTab` stays for the flag-off pill (BAR-2853 removes it).
 *
 * No Status column or filter: a booked consult locks the status. PN defaults to
 * your own patients when you're a PN. The filters run on the server (BAR-2884).
 */

const DEFAULT_PRESET = 'upcoming';

const CONSULT_PRESETS = [
  { id: 'upcoming' as const, label: 'All upcoming' },
  { id: 'next-7' as const, label: 'Next 7 days' },
  { id: 'next-30' as const, label: 'Next 30 days' },
];

type HubPreConsultationRow = EditedRow<PreConsultationLeadRow>;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface PreConsultationTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function PreConsultationTab({ now }: PreConsultationTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);

  const filters = usePatientListFilters<PreConsultationLeadRow>({
    resetKey: 'intake-pre-consultation',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const { values, setValues, setFilter, defaults, pnOptions } = useHubFilterValues();

  const filter = useMemo(
    () => withPatientFilters(buildUpcomingConsultFilter(filters.range), values),
    [filters.range, values]
  );
  const subscriptionParams = useMemo(() => upcomingConsultSubscriptionParams(), []);

  const {
    rows: data,
    total,
    loading,
    error,
    page,
    setPage,
  } = useQueuePage(ALL_PRE_CONSULTATION_APPOINTMENTS_QUERY, 'Appointment', filter, subscriptionParams, 'date');

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the pre-consultation queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const appointments = useMemo(
    () =>
      error
        ? []
        : (data as PacQueueAppointmentResult[]).filter((appointment) =>
            isUpcomingConsult(appointment, filters.range, resolvedNow)
          ),
    [data, error, filters.range, resolvedNow]
  );

  const patientRefs = useMemo(
    () => [
      ...new Set(
        appointments
          .flatMap((appointment) => appointment.participant ?? [])
          .map((participant) => participant.actor?.resource)
          .filter((resource) => resource?.resourceType === 'Patient')
          .map((resource) => `Patient/${(resource as { id?: string }).id}`)
      ),
    ],
    [appointments]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    { careTeam: true, eligibility: true, stickyNotes: true, lastCommunicationSince: lastCommunicationFloor(today) },
    `intake-pre-consultation:${filter}`
  );

  const builtRows = useMemo(
    () => buildUpcomingConsultRows(appointments, hydration, resolvedNow),
    [appointments, hydration, resolvedNow]
  );
  const { rows, onNoteSaved } = useHubRowEdits(builtRows, QUEUE_FILTER_ANY);
  // Rows that arrive by subscription skip the query's filter, so they are checked on their tags too.
  const visibleRows = useMemo(
    () => rows.filter((row) => matchesPatientFilterTags(row.patient, values)),
    [rows, values]
  );

  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const columns = useMemo(
    () => [
      patientColumn<HubPreConsultationRow>(),
      paymentTrackColumn<HubPreConsultationRow>(),
      pnColumn<HubPreConsultationRow>(),
      upcomingConsultColumn<HubPreConsultationRow>(),
      lastContactColumn<HubPreConsultationRow>(),
      notesColumn<HubPreConsultationRow>({ onSaved: onNoteSaved }),
    ],
    [onNoteSaved]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="Consult"
            presets={CONSULT_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
        ]}
        isFiltered={isQueueFiltered(values, defaults) || filters.presetId !== DEFAULT_PRESET}
        onReset={() => {
          setValues(defaults);
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'consult', desc: false }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
      />
      <QueueFooter
        shown={visibleRows.length}
        total={total}
        page={page}
        pageCount={Math.ceil((total ?? 0) / QUEUE_PAGE_ROWS)}
        onPageChange={setPage}
      />
    </Box>
  );
}
